import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { io, Socket } from "socket.io-client";

import { useGetPresenceWatchMutation } from "../../store/api/liveLectureApi";
import { isPastLiveLecture } from "./liveLectureCalendarUtils";

export type CallPresence = {
  ongoing: boolean;
  participantCount: number;
  hostPresent: boolean;
};

type CallPresenceSnapshot = CallPresence & { roomId: string };

type WatchLecture = {
  id: string;
  meetingRoomId?: string;
  endAt: string | Date;
};

const emptyPresence: CallPresence = {
  ongoing: false,
  participantCount: 0,
  hostPresent: false,
};

export const lectureRoomId = (lecture: {
  id: string;
  meetingRoomId?: string;
}) => lecture.meetingRoomId || lecture.id;

/** Re-render lists when a lecture's scheduled end passes. */
export function useLiveLectureClock(intervalMs = 10000): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);

  return now;
}

/**
 * One staff socket for the visible lectures that have not ended.
 * The watcher is not counted as a person in the call.
 */
export function useLiveLecturePresence(lectures: WatchLecture[], now: number) {
  const key = useMemo(
    () =>
      lectures
        .filter((lecture) => lecture.id && !isPastLiveLecture(lecture.endAt, now))
        .map((lecture) => lecture.id)
        .sort()
        .join(","),
    [lectures, now],
  );
  const [byRoomId, setByRoomId] = useState<Record<string, CallPresence>>({});
  const [requestWatch] = useGetPresenceWatchMutation();

  useEffect(() => {
    const ids = key ? key.split(",") : [];
    if (!ids.length) {
      setByRoomId({});
      return;
    }

    let cancelled = false;
    let socket: Socket | null = null;

    const apply = (room: CallPresenceSnapshot) => {
      setByRoomId((current) => ({
        ...current,
        [room.roomId]: {
          ongoing: room.ongoing,
          participantCount: room.participantCount,
          hostPresent: room.hostPresent,
        },
      }));
    };

    const connect = async () => {
      try {
        const watch = await requestWatch({ ids }).unwrap();
        if (cancelled) {
          return;
        }

        socket?.removeAllListeners();
        socket?.disconnect();

        const next = io(`${watch.callingUrl}${watch.namespace}`, {
          transports: ["websocket", "polling"],
          auth: { token: watch.token },
          forceNew: true,
        });
        socket = next;

        const subscribe = () => {
          next.emit(
            "watchRooms",
            { roomIds: watch.rooms.map((room) => room.roomId) },
            (response: { rooms?: CallPresenceSnapshot[] } | undefined) => {
              const snapshots = response?.rooms ?? [];
              setByRoomId((current) => {
                const nextState = { ...current };
                for (const roomId of watch.rooms.map((room) => room.roomId)) {
                  nextState[roomId] = nextState[roomId] ?? emptyPresence;
                }
                for (const room of snapshots) {
                  nextState[room.roomId] = {
                    ongoing: room.ongoing,
                    participantCount: room.participantCount,
                    hostPresent: room.hostPresent,
                  };
                }
                return nextState;
              });
            },
          );
        };

        next.on("connect", subscribe);
        next.on("callPresence", apply);
      } catch {
        // Schedule-based labels still render when the watch token cannot be issued.
      }
    };

    void connect();
    const refreshTimer = window.setInterval(() => {
      void connect();
    }, 4 * 60 * 1000);

    return () => {
      cancelled = true;
      window.clearInterval(refreshTimer);
      socket?.removeAllListeners();
      socket?.disconnect();
    };
  }, [key, requestWatch]);

  return byRoomId;
}

export function LiveLecturePresenceLine({
  startAt,
  endAt,
  presence,
  now,
}: {
  startAt: string | Date;
  endAt: string | Date;
  presence?: CallPresence;
  now: number;
}) {
  const { t } = useTranslation();

  if (isPastLiveLecture(endAt, now)) {
    return (
      <div className="text-muted small">{t("liveLecturePresenceClosed")}</div>
    );
  }

  if (!presence?.ongoing) {
    const started = new Date(startAt).getTime() <= now;
    return (
      <div className="text-muted small">
        {started
          ? t("liveLecturePresenceEmpty")
          : t("liveLecturePresenceNotStarted")}
      </div>
    );
  }

  return (
    <div className="small">
      <div className="text-success">{t("liveLecturePresenceLive")}</div>
      <div>
        {t("liveLecturePresenceInCall", { count: presence.participantCount })}
      </div>
      <div>
        {presence.hostPresent
          ? t("liveLecturePresenceTeacherIn")
          : t("liveLecturePresenceTeacherOut")}
      </div>
    </div>
  );
}
